<template>
  <div class="container">
    <div class="login-wrapper">
      <div class="login-card">
        <div class="login-header">
          <h2>用户登录</h2>
          <p>欢迎使用智能设备后市场服务云平台</p>
        </div>
        <el-form
            ref="formRef"
            :model="formData"
            :rules="rules"
            class="login-form"
            @submit.prevent="handleSubmit"
        >
          <!-- 手机号 -->
          <el-form-item label="手机号" label-width="6rem" label-position="left" prop="telephone">
            <el-input
                v-model="formData.telephone"
                placeholder="请输入手机号"
                clearable
            />
          </el-form-item>

          <!-- 密码 -->
          <el-form-item label="密码" label-width="6rem" label-position="left" prop="password">
            <el-input
                v-model="formData.password"
                type="password"
                placeholder="请输入密码"
                show-password
                clearable
            >
            </el-input>
          </el-form-item>

          <!-- 协议同意 -->
          <AgreementCheckbox v-model="formData.agreed" />

          <!-- 登录按钮 -->
          <el-form-item>
            <el-button
                type="primary"
                class="login-btn"
                :disabled="!formData.agreed"
                @click="handleSubmit"
            >
              立即登录
            </el-button>
          </el-form-item>

          <!-- 忘记密码和注册链接 -->
          <div class="form-footer">
            <a class="forgot-link" @click="goToForgotPassword">忘记密码？</a>
            <span>还没有账号？ <a class="register-link" @click="goToRegister">立即注册</a></span>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  background: linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%);
}

.login-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1.5rem 1.25rem;
}

.login-card {
  width: 28rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
}

.login-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.login-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1890ff;
  margin: 0 0 0.4rem 0;
}

.login-header p {
  font-size: 0.875rem;
  color: #666;
  margin: 0;
}

.login-form {
  width: 100%;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 1.2rem;
}

.login-form :deep(.el-form-item__label) {
  font-size: 1rem;
  color: #333;
  font-weight: 500;
  line-height: 2.75rem;
  height: 2.75rem;
}

.login-form :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #d9d9d9 inset;
  border-radius: 8px;
  height: 2.75rem;
  padding: 0.3rem 0.45rem;
}

.login-form :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #40a9ff inset;
}

.login-form :deep(.el-input.is-focus .el-input__wrapper) {
  box-shadow: 0 0 0 1px #1890ff inset;
}

.login-form :deep(.el-input__inner) {
  font-size: 0.9375rem;
  line-height: 1.75rem;
  height: 1.75rem;
}

.password-toggle {
  color: #999;
  transition: color 0.3s;
}

.password-toggle:hover {
  color: #1890ff;
}

.login-form :deep(.el-button) {
  font-size: 0.9375rem;
  height: 2.75rem;
  padding: 0.5rem 1.5rem;
}

.forgot-link {
  font-size: 0.9375rem;
  color: #1890ff;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s;
}

.forgot-link:hover {
  color: #40a9ff;
}

.login-btn {
  width: 100%;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.2rem;
  font-size: 0.875rem;
  color: #666;
}

.register-link {
  color: #1890ff;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s;
}

.register-link:hover {
  color: #40a9ff;
}
</style>

<script setup>
import {reactive, ref} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {JSEncrypt} from 'jsencrypt'
import apiClient from "@/components/plugins/axios";
import {ElMessage} from "element-plus";
import AgreementCheckbox from '@/components/templates/AgreementCheckbox.vue'

const router = useRouter()
const route = useRoute()
const formRef = ref()
const submitTimer = ref(null)
const isSubmitting = ref(false)

const formData = reactive({
  telephone: '',
  password: '',
  agreed: false
})

// 验证规则
const rules = reactive({
  telephone: [
    {required: true, message: '请输入手机号', trigger: 'blur'},
    {pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur'}
  ],
  password: [
    {required: true, message: '请输入密码', trigger: 'blur'},
    {pattern: /^(?=.*[0-9])(?=.*[a-zA-Z]).{8,16}$/, message: '密码必须为8-16位，且同时包含数字和字母', trigger: 'blur'}
  ]
})

// 账号密码登录处理（带防抖）
async function handleSubmit() {
  // 防抖处理：如果正在提交，直接返回
  if (isSubmitting.value) {
    return
  }

  // 清除之前的定时器
  if (submitTimer.value) {
    clearTimeout(submitTimer.value)
  }

  // 设置防抖延迟
  submitTimer.value = setTimeout(async function() {
    if (isSubmitting.value) {
      return
    }
    isSubmitting.value = true

    try {
      await formRef.value.validate()
    } catch (error) {
      isSubmitting.value = false
      ElMessage({
        message: '请检查表单填写',
        type: 'error',
        duration: 1000
      });
      return
    }
    try {
      // 1. 获取加密公钥
      const keyRes = await apiClient.get('/api/v1/common/get/pubkey');

      if (!keyRes.data.public_key) {
        throw new Error('获取加密公钥失败');
      }

      // 2. 加密密码
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(keyRes.data.public_key);
      const encryptedPassword = encrypt.encrypt(formData.password).toString();

      // 3. 执行登录
      const loginRes = await apiClient.post('/api/v1/common/login', {
        telephone: formData.telephone,
        password: encryptedPassword,
        method: '8',
      }, {
        headers: {
          'login-role': 'Customer',
          'platform': '1',
          'method': '8',
        }
      });

      // 4. 处理登录结果
      if (loginRes.data.code !== 200) {
        throw new Error(loginRes.data.message || '登录失败');
      }

      // 5. 保存登录信息
      const {data} = loginRes.data;
      localStorage.setItem('access_token', `${data.token_type} ${data.access_token}`);
      localStorage.setItem('refresh_token', data.refresh_token);

      const userInfo = {
        id: data.id,
        telephone: data.telephone,
        gender: data.gender,
        nickname: data.nickname,
        avatar: data.avatar,
        birthday: data.birthday,
      };
      // 注意：存储对象需要序列化
      localStorage.setItem('userInfo', JSON.stringify(userInfo));

      ElMessage({
        message: '登录成功',
        type: 'success',
        duration: 1000
      });

      // 触发自定义事件，通知其他组件（如 HeaderView）更新用户信息
      window.dispatchEvent(new CustomEvent('auth-change'))

      // 7. 登录成功后跳转：如果有 redirect 参数，跳转回原页面，否则跳转到首页
      const redirectPath = route.query.redirect
      if (redirectPath && typeof redirectPath === 'string') {
        // 跳转回原来要访问的页面
        await router.push(redirectPath)
      } else {
        // 跳转到首页
        await router.push('/')
      }
      
      // 路由跳转完成后，再次触发事件，确保 HeaderView 能够更新
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('auth-change'))
      }, 100)

    } catch (error) {
      // 统一错误处理
      ElMessage({
        message: error.message || '网络请求异常，请稍后重试',
        type: 'error',
        duration: 1000
      });
    } finally {
      isSubmitting.value = false
    }
  }, 500)
}

// 跳转到忘记密码页面
function goToForgotPassword() {
  router.push('/forgotPassword')
}

// 跳转到注册页面
function goToRegister() {
  router.push('/register')
}

</script>